import { MenuItem, Paper, Stack, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CargoType,
  CarrierType,
  CarrierVisitStatus,
  ObjectType,
  OrderUpdateResponseDto,
  VisitDto,
} from '@planning/app/api'
import { CargoTypeIconLabel } from '@planning/components/CargoTypeIconLabel'
import { CarrierTypeIcon } from '@planning/components/CarrierTypeIcon'
import { CarrierVisitStatusChip } from '@planning/components/CarrierVisitStatusChip'
import { IssuesToResolveChip } from '@planning/components/IssuesToResolveChip'
import { GridColumn } from '@planning/components/PaginatedTable'
import { CellWithTitleAndSubtitle } from '@planning/components/atoms/CellWithTitleAndSubtitle'
import { emptyIndicator } from '@planning/constants'
import { vesselVisitDetailPages } from '@planning/pages/VesselVisit/Components/CarrierVisitDashboard'
import { UpdatesLabelChip } from '@planning/pages/VesselVisit/Components/atoms/UpdatesLabelChip'
import { VisitTableViewStore } from '@planning/rt-stores/visit/VisitTableViewStore'
import { VisitItem } from '@planning/rt-stores/visit/VisitViewStore'
import { carrierVisitService } from '@planning/services'
import { IPaginatedStoreWithItems, SortingModel } from '@planning/stores/PaginatedStore'
import { formatDayAndNumberWithAbbreviatedMonth, formatTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { ThreeDotMenu } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { VisitCarrierTypeFilter } from './VisitCarrierTypeFilter'
import { VisitCounts } from './VisitCounts'

interface Props {
  store: IPaginatedStoreWithItems<VisitItem>
  tableStore: VisitTableViewStore
}
export const VisitsTable = observer(({ store, tableStore }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const { appViewStore, issueItemStore, orderUpdateItemStore, visitViewStore } = usePlanningStore()

  useEffect(() => {
    issueItemStore.fetch()
  }, [issueItemStore])

  const onEdit = (visit: VisitItem) => {
    switch (visit.carrierType) {
      case CarrierType.Vessel:
        navigate(`${PATHS.visits.vesselPlanning.url}/${visit.id}`)
        break

      case CarrierType.Train:
        navigate(`${PATHS.visits.railPlanning.url}/${visit.id}`)
        break
    }
  }

  const onCancel = async (visit: VisitItem) => {
    try {
      const confirmed = await appViewStore.setOpenConfirmDialog(
        true,
        t(
          'doYouWantToCancelTheVisitAndDeleteAllAssociatedOrders',
          'Are you sure you want to cancel this visit? This action will also delete all associated orders.',
        ),
      )
      if (confirmed) {
        await carrierVisitService.delete(visit.id!)
        await visitViewStore.fetchVisits()

        appViewStore.setShowAlert(
          'success',
          t('VisitWasCancelledSuccessfully', 'Visit was cancelled successfully'),
        )
      }
    } catch (error) {
      appViewStore.setShowAlert('error', t('failedToCancel', 'Failed to cancel'))
    }
  }

  const onViewDashboard = (visit: VisitItem) => {
    switch (visit.carrierType) {
      case CarrierType.Vessel:
        navigate(`${PATHS.visits.vesselVisits.url}/${visit.id}/${vesselVisitDetailPages.dashboard}`)
        break

      case CarrierType.Train:
        navigate(`${PATHS.visits.railVisits.url}/${visit.id}/dashboard`)
        break
    }
  }

  const columns: GridColumn<VisitItem>[] = [
    {
      field: 'carrierName',
      headerName: t('carrier', 'Carrier'),
      minWidth: 200,
      renderCell: ({ row }) => {
        const hasIssues = _(issueItemStore.elements).some(
          e =>
            e.data.objectType === ObjectType.Order &&
            !!row.data?.orderIds.includes(e.data.objectId),
        )

        let isBerthConflicted = false
        if (row.carrierType === CarrierType.Vessel) {
          isBerthConflicted = (row as VisitDto)?.vesselConflicts?.isConflicted || false
        }

        let isTrackConflicted = false
        let isRailcarLengthConflict = false
        let hasAssignedRailcarsWithoutLength = false
        if (row.carrierType === CarrierType.Train) {
          const railVisit = row as VisitDto

          isTrackConflicted = railVisit?.railConflicts?.isConflicted || false
          isRailcarLengthConflict =
            railVisit?.railConflicts?.isInboundRailcarLengthConflicted ||
            railVisit?.railConflicts?.isOutboundRailcarLengthConflicted ||
            false

          hasAssignedRailcarsWithoutLength =
            railVisit?.railConflicts?.hasAssignedRailcarsWithoutLength || false
        }

        const orderUpdates = computed<OrderUpdateResponseDto[]>(() => {
          const orderUpdates = orderUpdateItemStore.unreadOrderUpdates.filter(i =>
            row?.orderIds.includes(i.orderId),
          )

          return orderUpdates
        }).get()

        return (
          <Stack direction='row' spacing={2} alignItems={'center'}>
            <CellWithTitleAndSubtitle
              title={row.carrierName}
              subtitle={row.shippingLine ?? emptyIndicator}
            />
            {(isBerthConflicted ||
              isTrackConflicted ||
              isRailcarLengthConflict ||
              hasAssignedRailcarsWithoutLength ||
              hasIssues) && <IssuesToResolveChip />}

            {row.carrierType === CarrierType.Vessel && orderUpdates.length > 0 && (
              <UpdatesLabelChip updateCount={orderUpdates.length} />
            )}
          </Stack>
        )
      },
      flex: 1,
      orderBy: 'carrierName',
    },
    {
      field: 'eta',
      headerName: t('timings', 'Timings'),
      orderBy: 'arrival',
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection='row' justifyContent='space-between' gap={2} width='100%'>
            <CellWithTitleAndSubtitle
              title={formatDayAndNumberWithAbbreviatedMonth(row.arrival) ?? emptyIndicator}
              subtitle={`${formatTime(row.arrival)} ${row?.ata ? t('ata', 'ATA') : t('eta', 'ETA')}`}
            />
            <CellWithTitleAndSubtitle
              title={formatDayAndNumberWithAbbreviatedMonth(row.departure) ?? emptyIndicator}
              subtitle={`${formatTime(row.departure)} ${row?.atd ? t('atd', 'ATD') : t('etd', 'ETD')}`}
            />
          </Stack>
        )
      },
      minWidth: 210,
      flex: 0.5,
    },
    {
      field: 'carrierType',
      headerName: t('type', 'Type'),
      renderCell: ({ row }) => <CarrierTypeIcon type={row.carrierType} />,
      width: 70,
      sortable: false,
    },
    {
      field: 'cargoType',
      headerName: t('cargo', 'Cargo'),
      renderCell: ({ row }) => (
        <CargoTypeIconLabel cargoType={row.cargoType ?? CargoType.Unknown} showLabel={false} />
      ),
      width: 70,
      sortable: false,
    },
    {
      field: 'dischargeEstimate',
      headerName: `${t('in', 'In')} / ${t('out', 'Out')}`,
      valueGetter: ({ row }) => {
        const dischargeCount =
          row.dischargeCount && row.dischargeCount > 0
            ? row.dischargeCount
            : row.dischargeEstimate ?? 0

        const loadCount = row.loadCount && row.loadCount > 0 ? row.loadCount : row.loadEstimate ?? 0

        return `${dischargeCount} / ${loadCount}`
      },
      minWidth: 100,
      sortable: false,
      flex: 0.5,
    },
    {
      field: 'tripIds',
      headerName: t('tripIds', 'Trip IDs'),
      renderCell: ({ row }) => {
        const inboundTripIds =
          row.inboundTripIds && row.inboundTripIds.length > 0 && row.inboundTripIds[0] !== ''

        const outboundTripIds =
          row.outboundTripIds && row.outboundTripIds.length > 0 && row.outboundTripIds[0] !== ''

        return (
          <Stack>
            {inboundTripIds && (
              <Stack flexDirection='row' gap={1}>
                <Typography variant='body2'>{t('in', 'In')}:</Typography>
                <Typography variant='body2'>
                  {row?.inboundTripIds?.filter(id => id.trim() !== '').join(', ')}
                </Typography>
              </Stack>
            )}
            {outboundTripIds && (
              <Stack flexDirection='row' gap={1}>
                <Typography variant='body2'>{t('out', 'Out')}:</Typography>
                <Typography variant='body2'>
                  {row?.outboundTripIds?.filter(id => id.trim() !== '').join(', ')}
                </Typography>
              </Stack>
            )}
            {!inboundTripIds && !outboundTripIds && emptyIndicator}
          </Stack>
        )
      },
      minWidth: 100,
      sortable: false,
      flex: 0.75,
    },
    {
      field: 'berthOrTrack',
      headerName: t('berthOrTrack', 'Berth/Track'),
      minWidth: 20,
      sortable: false,
      flex: 0.5,
    },
    {
      field: 'status',
      orderBy: 'status',
      headerName: t('status', 'Status'),
      minWidth: 150,
      renderCell: ({ row }) => {
        return <CarrierVisitStatusChip status={row.status} isBerthed={row.isBerthed} />
      },
      flex: 0.5,
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 25,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack alignItems='end' width='100%' data-cy='visits-table-dots-menu'>
          <ThreeDotMenu>
            <MenuItem
              onClick={() => onViewDashboard(row)}
              data-cy='visits-table-dots-menu-view-dashboard'
            >
              {t('viewDashboard', 'View dashboard')}
            </MenuItem>

            <MenuItem
              onClick={() => onEdit(row)}
              data-cy='visits-table-dots-menu-edit'
              disabled={row.status === CarrierVisitStatus.Closed}
            >
              {t('editVisit', 'Edit visit')}
            </MenuItem>

            {row.status === CarrierVisitStatus.Expected ? (
              <MenuItem onClick={() => onCancel(row)} data-cy='visits-table-dots-menu-cancel'>
                {t('removeVisit', 'Remove visit')}
              </MenuItem>
            ) : (
              <></>
            )}
          </ThreeDotMenu>
        </Stack>
      ),
    },
  ]

  return (
    <Paper variant='elevation' elevation={1} sx={{ overflow: 'hidden' }}>
      <VisitCarrierTypeFilter store={tableStore} />
      <VisitCounts store={tableStore} />
      <DataGrid
        rows={tableStore.visitItems.slice(
          store.pageSize * store.currentPageIndex,
          store.pageSize * (store.currentPageIndex + 1),
        )}
        columns={columns}
        paginationModel={{ pageSize: store.pageSize, page: store.currentPageIndex }}
        rowCount={tableStore.visitItems.length}
        paginationMode='server'
        loading={appViewStore.isLoading}
        onPaginationModelChange={(model: { page: number; pageSize: number }) => {
          if (store.currentPageIndex !== model.page) store.setCurrentPageIndex(model.page)
          if (store.pageSize !== model.pageSize) store.setPageSize(model.pageSize)
        }}
        disableColumnFilter
        disableColumnMenu
        onRowClick={({ row }) => onViewDashboard(row)}
        sortModel={
          store.sortingModel.orderBy && store.sortingModel.field
            ? [
                {
                  field: store.sortingModel.field,
                  sort: store.sortingModel.isDescending ? 'desc' : 'asc',
                },
              ]
            : []
        }
        onSortModelChange={model => {
          if (!model.length) {
            store.setSortingModel({
              field: '',
              orderBy: undefined,
              isDescending: false,
            })
            return
          }

          const item = model[0]
          const column = columns.find(c => c.field === item.field)
          store.setSortingModel({
            field: column?.field,
            orderBy: column?.orderBy,
            isDescending: item.sort === 'desc',
          } as SortingModel<any>)
        }}
        onCellClick={(column, event) => {
          if (column.field === 'actions') {
            event.stopPropagation()
          }
          event.preventDefault()
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Paper>
  )
})
